import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, distinctUntilChanged } from 'rxjs/operators';
import { FilingFormState, FilingFormStateService } from './filing-form-state.service';
import { DEI, DEI_EE_MFRS } from '../../constants/qnames';
import { SHEET_KEYS } from '../../models/sheet-keys';
import { parseApiDateString } from '../../utils/date-string-coercion.util';
import { VisibilityCell, resolveDimensionVisibility } from './validators/dimension-visibility.util';
import { getDimensionVisibilityConfig } from './validators/dimension-visibility-registry';
import {
  MFRS_ACCOUNTING_STANDARD,
  MPERS_ACCOUNTING_STANDARD,
} from './validators/schema-picker.util';

/**
 * Cross-referenced data from Role-1 Filing Information
 * Auto-populated to subsequent roles to ensure consistency
 */
export interface CrossReferenceData {
  // Company identification
  companyName: string;
  registrationNumber: string;
  registrationNumberNew: string;

  // Financial period
  financialYearStartCurrent: string | null;
  financialYearEndCurrent: string | null;
  financialYearStartPrevious: string | null;
  financialYearEndPrevious: string | null;

  // Currency and rounding
  presentationCurrency: string;
  functionalCurrency: string;
  roundingLevel: string;

  // Accounting framework
  accountingStandard: string;
  typeOfSubmission: string;
  auditStatus: string;

  // Financial statement structure
  natureOfFinancialStatements: string; // "Consolidated" or "Separate"
  preparationType: string; // "1st time preparation of financial statements" or "Subsequent preparation of financial statements"

  // Company details
  originOfCompany: string;
  statusOfCompany: string;
  typeOfCompany: string;

  // Software metadata
  softwareName: string;
  taxonomyVersion: string;
}

@Injectable({
  providedIn: 'root',
})
export class CrossReferenceService {
  private stateService = inject(FilingFormStateService);

  private crossRefSubject = new BehaviorSubject<CrossReferenceData>(
    this.getInitialData()
  );

  /**
   * Observable stream of cross-referenced data
   * Components can subscribe to get auto-populated values
   */
  public crossRef$: Observable<CrossReferenceData> =
    this.crossRefSubject.asObservable();

  constructor() {
    // Watch the ACTIVE filing-info source on every state emission. Under
    // PR-D strict phase-aware hydration, only one of the two slots is
    // populated per conversion — `filingPrerequisites` (role 0) in Phase 1,
    // `filingInfo` (role 1) in Phase 2. Reading the static
    // `state.filingPrerequisites` slot here would yield `{}` in Phase 2 and
    // silently strip every cross-reference field (including
    // `accountingStandard`), causing downstream consumers like
    // `pickRole18SchemaFile()` to fall through to its default schema and
    // build the form against the wrong qname prefix. (As of the
    // dimension-visibility convergence `pickRole18SchemaFile` is MFRS-default
    // like every other variant sheet, but it is still wrong to feed it an
    // empty standard.) Mirror `getInitialData()` so both initial and reactive
    // paths agree.
    this.stateService.state$
      .pipe(
        map(() =>
          this.extractCrossReferenceData(
            this.stateService.getActiveFilingInfoData()
          )
        ),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        )
      )
      .subscribe((crossRefData) => {
        this.crossRefSubject.next(crossRefData);

        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log('Cross-reference data updated:', crossRefData);
      });

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('CrossReferenceService initialized');
  }

  /**
   * Get current snapshot of cross-reference data
   */
  getCrossReferenceData(): CrossReferenceData {
    return this.crossRefSubject.value;
  }

  /**
   * Column-visibility cells for a dimension-gated sheet, or [] when the sheet is
   * not gated. Feeds the SAME pure resolver the validators use (prereq-first,
   * then filingInfo) so the mounted and headless verdicts cannot drift. Both
   * filing slices are passed; the resolver applies the precedence.
   */
  getVisibility(sheetKey: keyof FilingFormState): VisibilityCell[] {
    const config = getDimensionVisibilityConfig(sheetKey);
    if (!config)
      return [];
    
    return resolveDimensionVisibility(
      config, 
      {
        filingPrerequisites: this.stateService.getSheetData(SHEET_KEYS.filingPrerequisites),
        filingInfo: this.stateService.getSheetData(SHEET_KEYS.filingInfo),
      }
    );
  }

  /**
   * Observable for specific cross-reference field
   */
  getField<K extends keyof CrossReferenceData>(
    fieldName: K
  ): Observable<CrossReferenceData[K]> {
    return this.crossRef$.pipe(
      map((data) => data[fieldName]),
      distinctUntilChanged()
    );
  }

  /**
   * Get company name observable
   */
  getCompanyName$(): Observable<string> {
    return this.getField('companyName');
  }

  /**
   * Get registration number observable
   */
  getRegistrationNumber$(): Observable<string> {
    return this.getField('registrationNumber');
  }

  /**
   * Get accounting standard observable
   */
  getAccountingStandard$(): Observable<string> {
    return this.getField('accountingStandard');
  }

  /**
   * Get presentation currency observable
   */
  getPresentationCurrency$(): Observable<string> {
    return this.getField('presentationCurrency');
  }

  /**
   * Get financial year end (current) observable
   */
  getFinancialYearEndCurrent$(): Observable<string | null> {
    return this.getField('financialYearEndCurrent');
  }

  /**
   * Extract cross-reference data from Role-1 filing info
   */
  private extractCrossReferenceData(
    filingData: Record<string, any>
  ): CrossReferenceData {
    return {
      // Company identification
      companyName: filingData[DEI.NameOfReportingEntity] || '',
      registrationNumber: filingData[DEI.CompanyRegistrationNumber] || '',
      registrationNumberNew:
        filingData[DEI.NewCompanyRegistrationNumber] || '',

      // Financial period
      financialYearStartCurrent:
        filingData[DEI.CompanyCurrentFinancialYearStartDate] || null,
      financialYearEndCurrent:
        filingData[DEI.CompanyCurrentFinancialYearEndDate] || null,
      financialYearStartPrevious:
        filingData[DEI.CompanyPreviousFinancialYearStartDate] || null,
      financialYearEndPrevious:
        filingData[DEI.CompanyPreviousFinancialYearEndDate] || null,

      // Currency and rounding
      presentationCurrency:
        filingData[DEI.DescriptionOfPresentationCurrency] || 'MYR',
      functionalCurrency:
        filingData[DEI.DescriptionOfFunctionalCurrency] || 'MYR',
      roundingLevel:
        filingData[DEI.LevelOfRoundingUsedInFinancialStatements] || 'Actuals',

      // Accounting framework
      accountingStandard:
        filingData[
          DEI.BasisOfAccountingStandardsAppliedToPrepareFinancialStatements
        ] || '',
      typeOfSubmission: filingData[DEI.TypeOfSubmission] || '',
      auditStatus:
        filingData[DEI.DisclosureOfFinancialStatementsAuditStatus] || '',

      // Financial statement structure
      natureOfFinancialStatements:
        filingData[DEI_EE_MFRS.NatureOfFinancialStatements] || '',
      preparationType:
        filingData[
          DEI.DisclosureOfFinancialStatementsPreparationForCurrentSubmission
        ] || '',

      // Company details
      originOfCompany: filingData[DEI.OriginOfCompany] || '',
      statusOfCompany: filingData[DEI.StatusOfCompany] || '',
      typeOfCompany: filingData[DEI.TypeOfCompany] || '',

      // Software metadata
      softwareName:
        filingData[DEI.NameAndVersionOfSoftwareUsedToGenerateXBRLFile] ||
        'MBRS Filing Tool v1.0',
      taxonomyVersion:
        filingData[DEI.TaxonomyVersion] || 'SSM XBRL Taxonomy 2022 v1.0',
    };
  }

  /**
   * Get initial data from localStorage
   */
  private getInitialData(): CrossReferenceData {
    // Read from the active filing-info source (role0 in Phase 1, role1 in Phase 2).
    const filingData = this.stateService.getActiveFilingInfoData();
    return this.extractCrossReferenceData(filingData);
  }

  /**
   * Format financial year period for display
   * @returns e.g., "01/01/2024 - 31/12/2024"
   */
  getFormattedFinancialYear(
    period: 'current' | 'previous' = 'current'
  ): string {
    const data = this.getCrossReferenceData();

    if (period === 'current') {
      const start = data.financialYearStartCurrent;
      const end = data.financialYearEndCurrent;
      if (start && end) {
        return `${this.formatDate(start)} - ${this.formatDate(end)}`;
      }
    } else {
      const start = data.financialYearStartPrevious;
      const end = data.financialYearEndPrevious;
      if (start && end) {
        return `${this.formatDate(start)} - ${this.formatDate(end)}`;
      }
    }

    return '';
  }

  /**
   * Format date for display in DD/MM/YYYY format
   * @param dateStr Date string in YYYY-MM-DD or DD/MM/YYYY format
   * @returns Formatted date string (e.g., "01/01/2024")
   */
  private formatDate(dateStr: string): string {
    if (!dateStr) return '';

    // If already in DD/MM/YYYY format, return as-is
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
      return dateStr;
    }

    try {
      // Handle YYYY-MM-DD format or other ISO formats
      const date = new Date(dateStr);

      // Check if date is valid
      if (isNaN(date.getTime())) {
        return dateStr;
      }

      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Month is 0-indexed
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return dateStr;
    }
  }

  /**
   * Get currency symbol for presentation
   */
  getCurrencySymbol(): string {
    const currency = this.getCrossReferenceData().presentationCurrency;
    const symbols: { [key: string]: string } = {
      MYR: 'RM',
      USD: '$',
      EUR: '€',
      GBP: '£',
      SGD: 'S$',
      AUD: 'A$',
    };
    return symbols[currency] || currency;
  }

  /**
   * Get currency for presentation
   */
  getCurrency(): string {
    const currency = this.getCrossReferenceData().presentationCurrency;
    return currency || 'MYR';
  }

  /**
   * Get rounding level observable
   * @returns Observable of rounding level (e.g., "No rounding", "Thousands", "Millions")
   */
  getRoundingLevel$(): Observable<string> {
    return this.getField('roundingLevel');
  }

  /**
   * Get current rounding level
   * @returns Rounding level (e.g., "Actuals", "In thousands ('000')", "In millions ('000,000')")
   */
  getRoundingLevel(): string {
    return this.getCrossReferenceData().roundingLevel;
  }

  /**
   * Get currency with rounding unit for display
   * @returns Formatted string (e.g., "RM'000", "RM'000,000", "RM")
   */
  getCurrencyWithRounding(): string {
    const currency = this.getCurrency();
    const rounding = this.getRoundingLevel();

    // Map rounding level to display format
    const roundingMap: { [key: string]: string } = {
      Actuals: `${currency}'Actuals`,
      "In thousands ('000')": `${currency}'000`,
      "In millions ('000,000')": `${currency}'000000`,
      "In billions ('000,000,000')": `${currency}'000000000`,
    };

    return roundingMap[rounding] || currency;
  }

  /**
   * Get rounding format for display (without currency symbol)
   * @returns Rounding format (e.g., "Actuals", "000", "000000", "000000000")
   */
  getRoundingFormat(): string {
    const rounding = this.getRoundingLevel();

    // Map rounding level to display format
    const roundingMap: { [key: string]: string } = {
      Actuals: 'Actuals',
      "In thousands ('000')": '000',
      "In millions ('000,000')": '000000',
      "In billions ('000,000,000')": '000000000',
    };

    return roundingMap[rounding] || 'Actuals';
  }

  /**
   * Check if company uses MFRS (full IFRS compliance)
   */
  isMFRS(): boolean {
    return (
      this.getCrossReferenceData().accountingStandard ===
      MFRS_ACCOUNTING_STANDARD
    );
  }

  /**
   * Check if company uses MPERS (simplified standards). Uses the shared plural
   * "Standards" literal — the exact value the backend emits. (A previous
   * singular "Entity" spelling here never matched the backend; it was a latent
   * bug.)
   */
  isMPERS(): boolean {
    return (
      this.getCrossReferenceData().accountingStandard ===
      MPERS_ACCOUNTING_STANDARD
    );
  }

  /**
   * Check if submission is KFI (Key Financial Items only)
   */
  isKFI(): boolean {
    return this.getCrossReferenceData().typeOfSubmission.startsWith('KFI-');
  }

  /**
   * Check if submission is Full Statements
   */
  isFullStatements(): boolean {
    return this.getCrossReferenceData().typeOfSubmission.startsWith('FS-');
  }

  /**
   * Get period header for financial statements
   * @param period 'current' or 'previous'
   * @returns e.g., "For the year ended 31 December 2024"
   */
  getPeriodHeader(period: 'current' | 'previous' = 'current'): string {
    const data = this.getCrossReferenceData();

    if (period === 'current') {
      const endDate = data.financialYearEndCurrent;
      if (endDate) {
        return `For the year ended ${this.formatDateLong(endDate)}`;
      }
    } else {
      const endDate = data.financialYearEndPrevious;
      if (endDate) {
        return `For the year ended ${this.formatDateLong(endDate)}`;
      }
    }

    return '';
  }

  /**
   * Format date in long format for period headers
   * @param dateStr Date string: the API's day-first `dd/MM/yyyy`, or ISO
   *   `YYYY-MM-DD`. A `Date` object from the modified slice is accepted too.
   * @returns Formatted date string (e.g., "31 December 2024"), or the raw
   *   value when it cannot be parsed (never "NaN Invalid Date NaN").
   */
  private formatDateLong(dateStr: string | Date): string {
    // Parse day-first explicitly: a bare `new Date('05/03/2024')` is read
    // month-first by V8 (3 May), and `'31/12/2023'` is an Invalid Date, which
    // does not throw, so a try/catch could never catch it.
    const date =
      dateStr instanceof Date
        ? (isNaN(dateStr.getTime()) ? null : dateStr)
        : parseApiDateString(dateStr);
    if (!date) {
      return String(dateStr);
    }

    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'long' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  }

  /**
   * Check if financial statements are consolidated
   */
  isConsolidated(): boolean {
    return (
      this.getCrossReferenceData().natureOfFinancialStatements ===
      'Consolidated'
    );
  }

  /**
   * Check if financial statements are separate
   */
  isSeparate(): boolean {
    return (
      this.getCrossReferenceData().natureOfFinancialStatements === 'Separate'
    );
  }

  /**
   * Check if this is the first time preparation of financial statements
   */
  is1stTimePreparation(): boolean {
    return (
      this.getCrossReferenceData().preparationType ===
      'First time preparation of financial statements after incorporation'
    );
  }

  /**
   * Check if this is subsequent preparation of financial statements
   */
  isSubsequentPreparation(): boolean {
    return (
      this.getCrossReferenceData().preparationType ===
      'Subsequent preparation of financial statements'
    );
  }

  /**
   * Generate CalculatedSum vs ExtractedSum mismatch error message.
   * @param extractedSum - The sum from API/baseline (ExtractedSum)
   * @param calculatedSum - The sum from our calculations (CalculatedSum)
   * @param columnIndicator - Optional indicator for which column (e.g., "Consolidated Current", "Separate Previous")
   * @returns Error message with both values
   */
  generateMismatchError(
    extractedSum: any,
    calculatedSum?: any,
    columnIndicator?: string
  ): string {
    const formatValue = (val: any) => {
      return val?.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    };

    // Build column prefix if provided
    const columnPrefix = columnIndicator ? `${columnIndicator}: ` : '';

    // If calculatedSum is provided, show comparison
    if (calculatedSum !== undefined && calculatedSum !== null) {
      return `${columnPrefix}The line item total does not match the extracted sum of RM${formatValue(
        extractedSum
      )}. Please review and update the values if necessary to ensure the accuracy.`;
    }

    // Legacy format (backward compatibility)
    return `${columnPrefix}The line item total does not match the extracted sum of RM${formatValue(
      extractedSum
    )}. Please review and update the values if necessary to ensure the accuracy.`;
  }
}
